import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Loader2, ArrowRight, Check } from 'lucide-react';
import { useCRM } from '../lib/context';
import { stages } from '../lib/types';
import { today } from '../lib/api';
import { localDateTime as datetimeValue, fromLocalDateTime } from '../lib/dates';
import { Modal } from './Modal';
import { canWriteEntity } from '../lib/permissions';
import { WorkflowConditionEditor } from './WorkflowConditionEditor';

type Field = {
  name: string;
  label: string;
  type?: string;
  options?: string[];
  required?: boolean;
  placeholder?: string;
  full?: boolean;
  min?: number;
  max?: number;
  help?: string;
};
const definitions: Record<string, { title: string; description: string; fields: Field[] }> = {
  contacts: {
    title: 'A new relationship',
    description: 'Start with what you know. Build the picture over time.',
    fields: [
      { name: 'name', label: 'Full name', required: true, placeholder: 'e.g. Arjun Sharma' },
      { name: 'company', label: 'Company', placeholder: 'Company or organization' },
      {
        name: 'email',
        label: 'Email address',
        type: 'email',
        help: 'An email address or a phone number is required.',
      },
      { name: 'phone', label: 'Phone number', type: 'tel', placeholder: '+91' },
      { name: 'role', label: 'Professional role' },
      { name: 'owner', label: 'Relationship advisor' },
      {
        name: 'source',
        label: 'Source',
        options: [
          'Manual entry',
          'Website enquiry',
          'Referral',
          'Google Ads',
          'Event',
          'Manual research',
          'Import',
        ],
      },
      {
        name: 'sourceClass',
        label: 'Source classification',
        options: ['unknown', 'first_party', 'referral', 'public_business', 'licensed_enrichment'],
      },
      { name: 'budgetMin', label: 'Budget from (₹)', type: 'number' },
      { name: 'budgetMax', label: 'Budget to (₹)', type: 'number' },
      { name: 'localities', label: 'Preferred localities', placeholder: 'Kokapet, Gachibowli' },
      {
        name: 'configuration',
        label: 'Configuration',
        options: ['3 BHK', '4 BHK', '5 BHK', 'Villa', 'Commercial'],
      },
      { name: 'notes', label: 'Relationship notes', type: 'textarea', full: true },
    ],
  },
  opportunities: {
    title: 'Create an opportunity',
    description: 'Connect the right person with their next possibility.',
    fields: [
      { name: 'title', label: 'Opportunity title', required: true, full: true },
      { name: 'contactId', label: 'Contact', type: 'contact', required: true },
      { name: 'propertyId', label: 'Property', type: 'property' },
      { name: 'stage', label: 'Pipeline stage', options: stages },
      { name: 'value', label: 'Deal value (₹)', type: 'number', required: true },
      { name: 'probability', label: 'Probability (%)', type: 'number', max: 100 },
      { name: 'expectedRevenue', label: 'Expected commission (₹)', type: 'number' },
      { name: 'owner', label: 'Relationship advisor' },
      { name: 'nextAction', label: 'Next action' },
      { name: 'nextActionAt', label: 'Follow up on', type: 'datetime-local' },
      {
        name: 'lostReason',
        label: 'Reason if closed lost',
        placeholder: 'Required when closing this opportunity as lost',
        full: true,
      },
    ],
  },
  properties: {
    title: 'Add to the portfolio',
    description: 'Verified information makes better recommendations.',
    fields: [
      { name: 'name', label: 'Property / project name', required: true },
      { name: 'developer', label: 'Developer' },
      { name: 'locality', label: 'Locality', required: true },
      {
        name: 'type',
        label: 'Property type',
        options: ['Apartment', 'Villa', 'Penthouse', 'Commercial'],
      },
      { name: 'bhk', label: 'Configuration', options: ['3 BHK', '4 BHK', '5 BHK', '6 BHK'] },
      { name: 'price', label: 'Total price (₹)', type: 'number', required: true },
      {
        name: 'area',
        label: 'Area (sq. ft.)',
        type: 'number',
        required: true,
        min: 0.01,
        max: 1000000,
      },
      { name: 'possession', label: 'Possession', placeholder: 'Ready to move' },
      { name: 'possessionDate', label: 'Expected possession date', type: 'date' },
      {
        name: 'suitablePurposes',
        label: 'Suitable purposes',
        placeholder: 'End use, Investment',
        help: 'Separate purposes with commas.',
      },
      {
        name: 'financingOptions',
        label: 'Financing options',
        placeholder: 'Home loan, Self funded',
        help: 'Record only confirmed financing options.',
      },
      {
        name: 'availability',
        label: 'Availability',
        options: ['Available', 'Limited', 'Verify', 'Unavailable', 'Archived'],
      },
      {
        name: 'qualityState',
        label: 'Quality state',
        options: ['Draft', 'Verified', 'Published', 'Archived'],
      },
      { name: 'rera', label: 'RERA registration' },
      { name: 'sourceRef', label: 'Inventory source reference' },
      { name: 'verifiedAt', label: 'Last availability verification', type: 'datetime-local' },
      {
        name: 'image',
        label: 'Property image URL',
        type: 'url',
        placeholder: 'https://…',
        help: 'Use a publicly accessible image you have permission to use.',
      },
      { name: 'amenities', label: 'Amenities', placeholder: 'Pool, Clubhouse, Gym', full: true },
      { name: 'description', label: 'Description', type: 'textarea', full: true },
    ],
  },
  tasks: {
    title: 'Make the next move',
    description: 'A clear next action keeps relationships moving.',
    fields: [
      { name: 'title', label: 'Task', required: true, full: true },
      { name: 'contactId', label: 'Related contact', type: 'contact' },
      { name: 'priority', label: 'Priority', options: ['High', 'Medium', 'Low'] },
      { name: 'dueAt', label: 'Due date & time', type: 'datetime-local', required: true },
      { name: 'owner', label: 'Assigned advisor' },
      {
        name: 'type',
        label: 'Task type',
        options: ['Follow-up', 'Call', 'Visit', 'Verification', 'Document', 'Inventory'],
      },
    ],
  },
  visits: {
    title: 'Plan a private viewing',
    description: 'Bring your client one step closer to the right home.',
    fields: [
      { name: 'contactId', label: 'Contact', type: 'contact', required: true },
      { name: 'propertyId', label: 'Property', type: 'property', required: true },
      { name: 'date', label: 'Viewing date', type: 'date', required: true },
      { name: 'time', label: 'Time', type: 'time', required: true },
      { name: 'owner', label: 'Hosting advisor' },
      { name: 'status', label: 'Status', options: ['Scheduled', 'Confirmed'] },
      { name: 'notes', label: 'Preparation notes', type: 'textarea', full: true },
    ],
  },
  campaigns: {
    title: 'Create a campaign',
    description: 'Thoughtful outreach, to the right audience.',
    fields: [
      { name: 'name', label: 'Campaign name', required: true, full: true },
      {
        name: 'channel',
        label: 'Channel',
        options: ['Google Ads', 'Meta Ads', 'Email', 'WhatsApp', 'Referral'],
      },
      { name: 'budget', label: 'Campaign budget (₹)', type: 'number' },
      {
        name: 'audienceClass',
        label: 'Audience class',
        options: ['first_party', 'referral', 'public_business', 'licensed_enrichment', 'unknown'],
      },
      { name: 'locality', label: 'Target locality' },
      { name: 'utmSource', label: 'UTM source', placeholder: 'e.g. google' },
      { name: 'utmMedium', label: 'UTM medium', placeholder: 'e.g. cpc' },
      {
        name: 'utmCampaign',
        label: 'UTM campaign',
        placeholder: 'e.g. kokapet_private_viewings',
        full: true,
      },
    ],
  },
  suppliers: {
    title: 'Add a supply partner',
    description: 'Build a trusted network behind every property.',
    fields: [
      { name: 'name', label: 'Partner name', required: true },
      {
        name: 'type',
        label: 'Partner type',
        options: ['Developer', 'Seller', 'Channel partner', 'Legal', 'Finance', 'Interiors'],
      },
      { name: 'contact', label: 'Contact information' },
      { name: 'properties', label: 'Reported properties', type: 'number' },
      {
        name: 'mandate',
        label: 'Mandate',
        options: ['Exclusive', 'Non-exclusive', 'In discussion'],
      },
      { name: 'status', label: 'Status', options: ['Active', 'Pending', 'Inactive'] },
    ],
  },
  workflows: {
    title: 'Create an automation',
    description: 'A transparent rule with a clear outcome.',
    fields: [
      { name: 'name', label: 'Workflow name', required: true, full: true },
      {
        name: 'trigger',
        label: 'When this happens',
        options: [
          'LeadQualified',
          'SiteVisitScheduled',
          'TaskOverdue',
          'ScoreBandChanged',
          'PropertyAvailabilityChanged',
        ],
      },
      { name: 'condition', label: 'Condition', placeholder: 'BOS ≥ 70' },
      {
        name: 'action',
        label: 'Then do this',
        options: ['Create follow-up task', 'Refresh score', 'Request approval'],
      },
    ],
  },
};
const optionLabels: Record<string, string> = {
  first_party: 'First-party data',
  referral: 'Referral',
  public_business: 'Public business data',
  licensed_enrichment: 'Licensed enrichment',
  unknown: 'Unknown / unclassified',
  LeadQualified: 'Lead becomes qualified',
  SiteVisitScheduled: 'A site visit is scheduled',
  TaskOverdue: 'A task becomes overdue',
  ScoreBandChanged: 'Buyer score changes',
  PropertyAvailabilityChanged: 'Property availability changes',
};
const entityLabels: Record<string, string> = {
  contacts: 'relationship',
  properties: 'property',
  opportunities: 'opportunity',
  tasks: 'task',
  visits: 'site visit',
  campaigns: 'campaign',
  suppliers: 'partner',
  workflows: 'workflow',
};

export function RecordForm({
  type,
  defaults = {},
  onClose,
}: {
  type: string;
  defaults?: Record<string, any>;
  onClose: () => void;
}) {
  const { data, mutate, notify } = useCRM();
  const def = definitions[type] || definitions.contacts;
  const editing = Boolean(defaults.id);
  const canEdit = canWriteEntity(data.user, type, defaults?.id ? defaults : undefined);
  const entityLabel = entityLabels[type] || 'record';
  const [values, setValues] = useState<Record<string, any>>(() =>
    Object.fromEntries(
      def.fields.map((field) => {
        let value = defaults[field.name];
        if (value == null)
          value =
            field.name === 'owner'
              ? data.contacts.find((contact) => contact.id === defaults.contactId)?.owner ||
                data.user.name
              : field.name === 'condition'
                ? 'All matching records'
                : field.name === 'date'
                  ? today()
                  : field.name === 'time'
                    ? '10:00'
                    : field.name === 'priority'
                      ? 'Medium'
                      : field.name === 'stage'
                        ? 'Qualified'
                        : (field.options?.[0] ?? '');
        if (Array.isArray(value)) value = value.join(', ');
        if (field.type === 'datetime-local') value = datetimeValue(value);
        return [field.name, value];
      }),
    ),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const formRef = useRef<HTMLFormElement>(null);
  useEffect(() => {
    if (Object.keys(fieldErrors).length)
      formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
  }, [fieldErrors]);

  function change(name: string, value: string) {
    setValues((previous) => ({
      ...previous,
      [name]: value,
      ...(!editing && name === 'contactId'
        ? { owner: data.contacts.find((contact) => contact.id === value)?.owner || data.user.name }
        : {}),
      ...(type === 'workflows' && name === 'trigger' ? { condition: 'All matching records' } : {}),
    }));
    setFieldErrors((previous) => {
      const next = { ...previous };
      delete next[name];
      if (type === 'contacts' && (name === 'email' || name === 'phone')) {
        delete next.email;
        delete next.phone;
      }
      return next;
    });
    setError('');
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy || !canEdit) return;
    setError('');
    setFieldErrors({});
    const body: Record<string, any> = {};
    const issues: Record<string, string[]> = {};
    for (const field of def.fields) {
      const raw = values[field.name];
      let value = typeof raw === 'string' ? raw.trim() : raw;
      if (field.type === 'number') {
        value = Number(value || 0);
        if (
          !Number.isFinite(value) ||
          value < (field.min ?? 0) ||
          (field.max != null && value > field.max)
        )
          issues[field.name] = [
            `Enter a number from ${field.min ?? 0}${field.max != null ? ` to ${field.max}` : ' or higher'}.`,
          ];
      }
      if (['localities', 'amenities', 'suitablePurposes', 'financingOptions'].includes(field.name))
        value = String(value || '')
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean);
      if (field.type === 'datetime-local' && value) {
        try {
          value = fromLocalDateTime(String(value));
        } catch {
          issues[field.name] = ['Enter a valid date and time in IST.'];
        }
      }
      if (['name', 'title'].includes(field.name) && String(value).length < 2)
        issues[field.name] = ['Enter at least 2 characters.'];
      body[field.name] = value;
    }
    if (type === 'contacts') {
      if (!body.email && !body.phone) {
        issues.email = ['Add an email address or a phone number.'];
        issues.phone = ['Add an email address or a phone number.'];
      }
      if (body.phone && !/^\+?[\d ()-]{8,25}$/.test(body.phone))
        issues.phone = ['Enter a valid phone number, including country code when available.'];
      if (body.budgetMax > 0 && body.budgetMin > body.budgetMax)
        issues.budgetMax = ['The upper budget must be at least the lower budget.'];
      if (!editing) body.consent = { email: false, phone: false, whatsapp: false, ads: false };
    }
    if (type === 'opportunities' && body.stage === 'Closed Lost' && !body.lostReason)
      issues.lostReason = ['Record a reason for closing this opportunity as lost.'];
    if (type === 'campaigns' && !editing) body.status = 'Draft';
    if (Object.keys(issues).length) {
      setFieldErrors(issues);
      setError('A few details need your attention. Review the fields below.');
      return;
    }
    if (editing) body.version = defaults.version;
    setBusy(true);
    try {
      await mutate(
        editing ? `/${type}/${defaults.id}` : `/${type}`,
        editing ? 'PATCH' : 'POST',
        body,
      );
      notify(
        `${entityLabel.charAt(0).toUpperCase() + entityLabel.slice(1)} ${editing ? 'updated' : 'created'}`,
      );
      onClose();
    } catch (cause) {
      const failure = cause as Error & {
        details?: { fieldErrors?: Record<string, string[]>; formErrors?: string[] };
        code?: string;
      };
      const errors = failure.details?.fieldErrors || {};
      setFieldErrors(errors);
      const descriptions = Object.entries(errors).map(
        ([name, messages]) =>
          `${def.fields.find((field) => field.name === name)?.label || name}: ${messages.join(' ')}`,
      );
      const formErrors = failure.details?.formErrors || [];
      setError(
        descriptions.length || formErrors.length
          ? [...descriptions, ...formErrors].join(' ')
          : failure.message || 'The record could not be saved. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={editing ? `Edit ${entityLabel}` : def.title}
      description={editing ? 'Keep your workspace accurate and up to date.' : def.description}
      onClose={onClose}
    >
      <form ref={formRef} onSubmit={submit}>
        {error && (
          <p className="form-error" role="alert" style={{ marginBottom: 20 }}>
            {error}
          </p>
        )}
        <div className="form-grid">
          {def.fields.map((field) => {
            if (type === 'workflows' && field.name === 'condition')
              return (
                <WorkflowConditionEditor
                  key={field.name}
                  trigger={values.trigger}
                  value={values.condition}
                  onChange={(value) => change('condition', value)}
                />
              );
            const errorId = `record-${type}-${field.name}-error`;
            const hasError = Boolean(fieldErrors[field.name]?.length);
            const accessibility = {
              'aria-invalid': hasError,
              'aria-describedby': hasError ? errorId : undefined,
            };
            const choices =
              field.options && values[field.name] && !field.options.includes(values[field.name])
                ? [String(values[field.name]), ...field.options]
                : field.options;
            return (
              <label key={field.name} className={`field ${field.full ? 'field-full' : ''}`}>
                <span>
                  {field.label}
                  {field.required && <b> *</b>}
                </span>
                {field.type === 'textarea' ? (
                  <textarea
                    name={field.name}
                    {...accessibility}
                    rows={3}
                    maxLength={2000}
                    value={values[field.name]}
                    onChange={(e) => change(field.name, e.target.value)}
                  />
                ) : choices || ['contact', 'property'].includes(field.type || '') ? (
                  <select
                    name={field.name}
                    {...accessibility}
                    required={field.required}
                    value={values[field.name]}
                    onChange={(e) => change(field.name, e.target.value)}
                  >
                    {!choices && (
                      <option value="">
                        {field.required ? 'Select' : 'No linked'} {field.type}
                      </option>
                    )}
                    {choices
                      ? choices.map((option) => (
                          <option key={option} value={option}>
                            {optionLabels[option] || option.replaceAll('_', ' ')}
                          </option>
                        ))
                      : (field.type === 'contact' ? data.contacts : data.properties).map(
                          (record) => (
                            <option key={record.id} value={record.id}>
                              {record.name}
                            </option>
                          ),
                        )}
                  </select>
                ) : (
                  <input
                    name={field.name}
                    {...accessibility}
                    type={field.type || 'text'}
                    min={field.type === 'number' ? (field.min ?? 0) : undefined}
                    max={field.max}
                    step={
                      field.type === 'number'
                        ? field.name === 'properties'
                          ? 1
                          : 'any'
                        : undefined
                    }
                    maxLength={
                      field.type === 'number'
                        ? undefined
                        : field.name === 'image'
                          ? 2000
                          : field.name === 'email'
                            ? 254
                            : field.name === 'phone'
                              ? 30
                              : 200
                    }
                    minLength={['name', 'title'].includes(field.name) ? 2 : undefined}
                    required={field.required}
                    placeholder={field.placeholder}
                    value={values[field.name]}
                    readOnly={field.name === 'owner'}
                    onChange={(e) => change(field.name, e.target.value)}
                  />
                )}
                {hasError ? (
                  <small id={errorId} style={{ color: '#a85544', fontSize: 10, lineHeight: 1.6 }}>
                    {fieldErrors[field.name].join(' ')}
                  </small>
                ) : field.help ? (
                  <small className="muted" style={{ fontSize: 10, lineHeight: 1.6 }}>
                    {field.help}
                  </small>
                ) : null}
              </label>
            );
          })}
        </div>
        {def.fields.some((field) => field.name === 'owner') && (
          <div className="form-note">
            The advisor follows the selected contact. Change ownership through Team &amp; work →
            Work assignments so linked work remains accessible.
          </div>
        )}
        {type === 'contacts' && !editing && (
          <div className="form-note">
            Channel permissions start unconfirmed. Record permission evidence in Contact 360 before
            outreach.
          </div>
        )}
        {type === 'properties' && (
          <div className="form-note">
            Published properties must pass the inventory quality check. Add verified pricing,
            availability, an image, and source details.
          </div>
        )}
        {type === 'campaigns' && !editing && (
          <div className="form-note">
            Your campaign starts as a draft. Review audience eligibility before activating it.
          </div>
        )}
        {def.fields.some((field) => field.type === 'datetime-local' || field.type === 'time') && (
          <div className="form-note">
            All dates and times use India Standard Time (Asia/Kolkata).
          </div>
        )}
        <div className="modal-footer">
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" disabled={busy || !canEdit}>
            {busy ? (
              <Loader2 size={16} className="spin" />
            ) : editing ? (
              <Check size={16} />
            ) : (
              <ArrowRight size={16} />
            )}{' '}
            {busy ? 'Saving…' : editing ? 'Save changes' : `Create ${entityLabel}`}
          </button>
        </div>
      </form>
    </Modal>
  );
}
